import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { Facebook, Twitter, Instagram, Youtube, Mail, Phone, Shield, FileText } from 'lucide-react';

const Footer: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();

  return (
    <footer className="bg-white border-t border-indigo-100 mt-20 relative overflow-hidden">
      {/* Decorative background blobs */}
      <div className="absolute top-0 left-1/4 w-64 h-64 bg-indigo-50/50 rounded-full blur-3xl -translate-y-1/2 pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-pink-50/50 rounded-full blur-3xl translate-y-1/2 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 mb-12">
          {/* Brand & Description */}
          <div className="col-span-1 sm:col-span-2 lg:col-span-2 space-y-6">
            <Link to="/" className="flex items-center gap-2 group">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center text-white shadow-lg shadow-indigo-200 group-hover:scale-105 transition-transform duration-300">
                <span className="font-bold text-xl">T</span>
              </div>
              <span className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-purple-600">
                TaoThaoAIPro
              </span>
            </Link>
            <p className="text-gray-500 text-sm leading-relaxed max-w-md">
              {t('footer.description')}
            </p>
            <div className="flex gap-4">
              <a href="#" className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 hover:bg-indigo-50 hover:text-indigo-600 transition-all duration-300 hover:scale-110 shadow-sm border border-slate-100">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 hover:bg-sky-50 hover:text-sky-500 transition-all duration-300 hover:scale-110 shadow-sm border border-slate-100">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 hover:bg-pink-50 hover:text-pink-600 transition-all duration-300 hover:scale-110 shadow-sm border border-slate-100">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 hover:bg-red-50 hover:text-red-600 transition-all duration-300 hover:scale-110 shadow-sm border border-slate-100">
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-slate-800 font-bold mb-6 flex items-center gap-2">
              <span className="w-1 h-6 bg-indigo-500 rounded-full"></span>
              {t('footer.quickLinks.title')}
            </h3>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-slate-500 hover:text-indigo-600 transition-colors text-sm flex items-center gap-2 group">
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-200 group-hover:bg-indigo-500 transition-colors"></span>
                  {t('footer.quickLinks.home')}
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-slate-500 hover:text-indigo-600 transition-colors text-sm flex items-center gap-2 group">
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-200 group-hover:bg-indigo-500 transition-colors"></span>
                  {t('footer.quickLinks.about')}
                </Link>
              </li>
              {user?.role === 'admin' && (
                <li>
                  <Link to="/classes" className="text-slate-500 hover:text-indigo-600 transition-colors text-sm flex items-center gap-2 group">
                    <span className="w-1.5 h-1.5 rounded-full bg-indigo-200 group-hover:bg-indigo-500 transition-colors"></span>
                    {t('footer.quickLinks.classes')}
                  </Link>
                </li>
              )}
              <li>
                <Link to="/community" className="text-slate-500 hover:text-indigo-600 transition-colors text-sm flex items-center gap-2 group">
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-200 group-hover:bg-indigo-500 transition-colors"></span>
                  {t('footer.quickLinks.community')}
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="text-slate-500 hover:text-indigo-600 transition-colors text-sm flex items-center gap-2 group">
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-200 group-hover:bg-indigo-500 transition-colors"></span>
                  {t('footer.quickLinks.pricing')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h3 className="text-slate-800 font-bold mb-6 flex items-center gap-2">
              <span className="w-1 h-6 bg-pink-500 rounded-full"></span>
              {t('footer.support.title')}
            </h3>
            <ul className="space-y-4">
              <li>
                <div className="flex items-start gap-3 text-slate-500 text-sm group">
                  <Mail className="w-5 h-5 text-indigo-400 group-hover:text-indigo-600 transition-colors mt-0.5" />
                  <span className="group-hover:text-indigo-600 transition-colors">support@taothaoaipro.com</span>
                </div>
              </li>
              <li>
                <div className="flex items-start gap-3 text-slate-500 text-sm group">
                  <Phone className="w-5 h-5 text-pink-400 group-hover:text-pink-600 transition-colors mt-0.5" />
                  <span className="group-hover:text-pink-600 transition-colors">+84 987 654 321</span>
                </div>
              </li>

            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-slate-100 pt-8 mt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-400 text-sm font-medium">
              &copy; {new Date().getFullYear()} <span className="text-indigo-600 font-bold">TaoThaoAIPro</span>. {t('footer.rights')}
            </p>
            <div className="flex gap-6 text-sm">
              <a href="#" className="text-slate-500 hover:text-indigo-600 transition-colors flex items-center gap-1.5">
                <Shield className="w-4 h-4" />
                {t('footer.privacy')}
              </a>
              <a href="#" className="text-slate-500 hover:text-indigo-600 transition-colors flex items-center gap-1.5">
                <FileText className="w-4 h-4" />
                {t('footer.terms')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
export default Footer;
